// eslint-disable-next-line no-unused-vars
import React from 'react';
import styled from '@emotion/styled';
import { func, bool, array, object } from 'prop-types';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { uniqueId } from 'lodash';
import {
  Button,
  Loading,
  SkeletonPlaceholder,
  SkeletonText,
} from '@carbon/react';

import { NWComboBox, NWTextInput, Can } from 'Components/shared';
import { CURRENCY_RON, PERMISSION } from 'Constants';

const StyledAccountSettings = styled.div`
  section {
    margin-bottom: 16px;
    border: 1px solid #e2e2e2;

    h4 {
      font-size: 16px;
      padding: 16px;
      background: #f5f7f9;
    }

    .sectionBody {
      display: flex;
      flex-wrap: wrap;
      gap: 16px;
      padding: 16px;

      &.column {
        flex-direction: column;
      }

      &.skeleton {
        flex-direction: column;
        
        > div {
          flex: auto;
        }
      }

      > div {
        flex: 1;
      }

      .fullWidth {
        width: 100%;
        flex: auto;
      }

      .cds--text-input-wrapper {
        margin-bottom: 0;
      }

      .extraLabel {
        .cds--label {
          margin-bottom: 21px;

          span {
            position: absolute;
            top: 18px;
            left: 0;
            font-size: 12px;
            color: #525252;
          }
        }
      }
    }
  }

  .submit {
    display: flex;
    justify-content: end;

    button {
      width: 100%;
      max-width: 255px;
      height: 40px !important;
      background-color: #da1e28;

      svg {
        height: 16px;
        margin-right: 16px;
      }
    }
  }

  @media screen and (max-width: ${ props => props.theme.device.laptop }) {
    .sectionBody {
      flex-direction: column;
    }

    .submit {
      button {
        width: 100%;
        max-width: none;
      }
    }
  }

  /* FIX: for extra label */
  @media screen and (max-width: 420px) {
    section {
      .sectionBody {
        .extraLabel {
          .cds--label {
            margin-bottom: 35px;
          }
        }
      }
    }
  }
`;

const StyledSkeletonPlaceholder = styled(SkeletonPlaceholder)`
  height: 3rem;
  width: 100%;
`;

const fields = {
  defaultCurrency: 'defaultCurrency',
  language: 'language',
  withVat: 'withVat',
  sessionLifetime: 'sessionLifetime',
  userPaymentOption: 'userPaymentOption',
  userTransportOption: 'userTransportOption',
  technicalSupportEmail: 'technicalSupportEmail',
  alertEmail: 'alertEmail',
  alertOption: 'alertOption',
  alertEmailLanguage: 'alertEmailLanguage',
  resellerPaymentOption: 'resellerPaymentOption',
  resellerTransportOption: 'resellerTransportOption',
};

const AccountSettings = ({
  loading,
  isAdmin,
  isExternalReseller,
  validators,
  register,
  control,
  watch,
  rhfErrors,
  handleOnChange,
  handleOnSubmit,
  submitLoading,
  currencyOptions,
  languageOptions,
  vatOptions,
  sessionLifetimeOptions,
  paymentOptions,
  transportOptions,
  emailAlertOptions,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const sharedProps = fieldName => ({
    id: fieldName,
    name: fieldName,
    invalid: !!rhfErrors[fieldName]?.message.length,
    invalidText: rhfErrors[fieldName]?.message,
    ...register(fieldName, { ...validators[fieldName] }),
  });

  const sharedComboBoxProps = fieldName => ({
    id: fieldName,
    name: fieldName,
    invalid: rhfErrors[fieldName]?.message.length,
    invalidText: rhfErrors[fieldName]?.message,
    control,
    rules: { ...validators[fieldName] }
  });

  return (
    <StyledAccountSettings theme={ theme }>
      { loading ? (
        Array.from({ length: 4 }, () => (
          <section key={ uniqueId('skeletonSection') }>
            <h4><SkeletonText /></h4>
            <div className="sectionBody skeleton">
              { Array.from({ length: 3 }, () => <StyledSkeletonPlaceholder key={ uniqueId('skeletonPlaceholder') } />)}
            </div>
          </section>
        ))
      ) : (
        <>
          <section>
            <h4>{ t('profile.accountSettings') }</h4>
            <div className="sectionBody">
              <div>
                <NWComboBox
                  titleText={ t(`profile.${ fields.defaultCurrency }`) }
                  placeholder={ t(`profile.${ fields.defaultCurrency }`) }
                  light
                  required
                  { ...sharedComboBoxProps(fields.defaultCurrency) }
                  onChange={ value => handleOnChange(fields.defaultCurrency, value) }
                  items={ (isExternalReseller ? currencyOptions.filter(item => item.value !== CURRENCY_RON) : currencyOptions) || [] }
                  initialSelectedItem={
                    (currencyOptions || []).find(item => item?.value === watch(fields.defaultCurrency)) || ''
                  }
                />
                <NWComboBox
                  titleText={ t(`profile.${ fields.language }`) }
                  placeholder={ t(`profile.${ fields.language }`) }
                  light
                  required
                  disabled={ isExternalReseller }
                  { ...sharedComboBoxProps(fields.language) }
                  onChange={ value => handleOnChange(fields.language, value) }
                  items={ languageOptions || [] }
                  initialSelectedItem={
                    (languageOptions || []).find(item => item?.value === watch(fields.language)) || ''
                  }
                />
              </div>
              <div>
                { !isExternalReseller && (
                  <NWComboBox
                    titleText={ t(`profile.${ fields.withVat }`) }
                    placeholder={ t(`profile.${ fields.withVat }`) }
                    light
                    required
                    { ...sharedComboBoxProps(fields.withVat) }
                    onChange={ value => handleOnChange(fields.withVat, value) }
                    items={ vatOptions || [] }
                    initialSelectedItem={
                      (vatOptions || []).find(item => item?.value === watch(fields.withVat)) || ''
                    }
                  />
                )}
                <NWComboBox
                  titleText={ t(`profile.${ fields.sessionLifetime }`) }
                  placeholder={ t(`profile.${ fields.sessionLifetime }`) }
                  light
                  required
                  { ...sharedComboBoxProps(fields.sessionLifetime) }
                  onChange={ value => handleOnChange(fields.sessionLifetime, value) }
                  items={ sessionLifetimeOptions || [] }
                  initialSelectedItem={
                    (sessionLifetimeOptions || []).find(item => item?.value === watch(fields.sessionLifetime)) || ''
                  }
                />
              </div>
            </div>
          </section>
          <Can permission={ PERMISSION.ACCESS_ACCOUNT_SETTINGS }>
            <section>
              <h4>{ t('profile.defaultOptions') }</h4>
              <div className="sectionBody">
                <div>
                  <NWComboBox
                    titleText={ t(`profile.${ fields.userPaymentOption }`) }
                    placeholder={ t(`profile.${ fields.userPaymentOption }`) }
                    light
                    required
                    { ...sharedComboBoxProps(fields.userPaymentOption) }
                    onChange={ value => handleOnChange(fields.userPaymentOption, value) }
                    items={ paymentOptions || [] }
                    initialSelectedItem={
                      (paymentOptions || []).find(item => item?.value === watch(fields.userPaymentOption)) || ''
                    }
                  />
                </div>
                <div>
                  <NWComboBox
                    titleText={ t(`profile.${ fields.userTransportOption }`) }
                    placeholder={ t(`profile.${ fields.userTransportOption }`) }
                    light
                    required
                    { ...sharedComboBoxProps(fields.userTransportOption) }
                    onChange={ value => handleOnChange(fields.userTransportOption, value) }
                    items={ transportOptions || [] }
                    initialSelectedItem={
                      (transportOptions || []).find(item => item?.value === watch(fields.userTransportOption)) || ''
                    }
                  />
                </div>
              </div>
            </section>
          </Can>
          { isAdmin && (
            <>
              <section>
                <h4>{ t('profile.emailAlertsOptions') }</h4>
                <div className="sectionBody">
                  <div className="fullWidth">
                    <NWTextInput
                      light
                      required
                      labelText={ t(`profile.${ fields.technicalSupportEmail }`) }
                      { ...sharedProps(fields.technicalSupportEmail) }
                    />
                    <NWTextInput
                      light
                      required
                      labelText={ t(`profile.${ fields.alertEmail }`) }
                      { ...sharedProps(fields.alertEmail) }
                    />
                  </div>
                  <div>
                    <NWComboBox
                      titleText={ t(`profile.${ fields.alertOption }`) }
                      placeholder={ t(`profile.${ fields.alertOption }`) }
                      light
                      required
                      { ...sharedComboBoxProps(fields.alertOption) }
                      onChange={ value => handleOnChange(fields.alertOption, value) }
                      items={ emailAlertOptions || [] }
                      initialSelectedItem={
                        (emailAlertOptions || []).find(item => item?.value === watch(fields.alertOption)) || ''
                      }
                    />
                  </div>
                  <div>
                    <NWComboBox
                      titleText={ t(`profile.${ fields.alertEmailLanguage }`) }
                      placeholder={ t(`profile.${ fields.alertEmailLanguage }`) }
                      light
                      required
                      disabled={ isExternalReseller }
                      { ...sharedComboBoxProps(fields.alertEmailLanguage) }
                      onChange={ value => handleOnChange(fields.alertEmailLanguage, value) }
                      items={ languageOptions || [] }
                      initialSelectedItem={
                        (languageOptions || []).find(item => item?.value === watch(fields.alertEmailLanguage)) || ''
                      }
                    />
                  </div>
                </div>
              </section>
              <section>
                <h4>{ t('profile.wideDefaultOptions') }</h4>
                <div className="sectionBody">
                  <div>
                    <NWComboBox
                      className="extraLabel"
                      titleText={ (
                        <>
                          { t(`profile.${ fields.resellerPaymentOption }`) }
                          <span>{ t(`profile.${ fields.resellerPaymentOption }Info`) }</span>
                        </>
                      ) }
                      placeholder={ t(`profile.${ fields.resellerPaymentOption }`) }
                      light
                      required
                      { ...sharedComboBoxProps(fields.resellerPaymentOption) }
                      onChange={ value => handleOnChange(fields.resellerPaymentOption, value) }
                      items={ paymentOptions || [] }
                      initialSelectedItem={
                        (paymentOptions || []).find(item => item?.value === watch(fields.resellerPaymentOption)) || ''
                      }
                    />
                  </div>
                  <div>
                    <NWComboBox
                      className="extraLabel"
                      titleText={ (
                        <>
                          { t(`profile.${ fields.resellerTransportOption }`) }
                          <span>{ t(`profile.${ fields.resellerTransportOption }Info`) }</span>
                        </>
                      ) }
                      placeholder={ t(`profile.${ fields.resellerTransportOption }`) }
                      light
                      required
                      { ...sharedComboBoxProps(fields.resellerTransportOption) }
                      onChange={ value => handleOnChange(fields.resellerTransportOption, value) }
                      items={ transportOptions || [] }
                      initialSelectedItem={
                        (transportOptions || []).find(item => item?.value === watch(fields.resellerTransportOption)) || ''
                      }
                    />
                  </div>
                </div>
              </section>
            </>
          )}
        </>
      )}
      <div className="submit">
        <Button
          className="primary"
          type="submit"
          disabled={ loading }
          onClick={ handleOnSubmit }
        >
          { submitLoading && <Loading active small withOverlay={ false } /> }
          { t('profile.saveSettings') }
        </Button>
      </div>
    </StyledAccountSettings>
  );
};

AccountSettings.propTypes = {
  loading: bool,
  isAdmin: bool,
  isExternalReseller: bool,
  validators: object,
  register: func,
  control: object,
  watch: func,
  rhfErrors: object,
  handleOnChange: func,
  handleOnSubmit: func,
  submitLoading: bool,
  currencyOptions: array,
  languageOptions: array,
  vatOptions: array,
  sessionLifetimeOptions: array,
  paymentOptions: array,
  transportOptions: array,
  emailAlertOptions: array,
};

AccountSettings.displayName = 'AccountSettingsComponent';

export default AccountSettings;
